import { isManager } from "@/lib/access";
import { HandoverDialog } from "@/components/HandoverDialog";
import { DeptFilterChip } from "@/components/DeptFilterChip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, ListChecks, Archive, ChevronRight, ChevronLeft, User, Users, Upload, Download } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  attended: "bg-sky-50 text-sky-700 border-sky-200",
  "in-progress": "bg-amber-50 text-amber-800 border-amber-200",
  awaiting: "bg-violet-50 text-violet-700 border-violet-200",
  "on-hold": "bg-orange-50 text-orange-700 border-orange-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function Tasks() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = isManager(user);
  const [tasks, setTasks] = useState([]);
  const [assignables, setAssignables] = useState([]);
  const [types, setTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(sp.get("view") === "completed" ? "completed" : "active");
  const [scope, setScope] = useState(() => {
    if (sp.get("scope") === "team") return "team";
    if (sp.get("scope") === "mine") return "mine";
    return "mine"; // will be flipped to team for admins after user loads
  });
  const [q, setQ] = useState(sp.get("q") || "");
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [assignee, setAssignee] = useState(sp.get("assignee") || "all");
  const [client, setClient] = useState(sp.get("client") || "");
  const [entity, setEntity] = useState(sp.get("entity") || "all");
  const [type, setType] = useState(sp.get("type") || "all");
  const [service, setService] = useState(sp.get("service") || "all");
  const [dueFilter, setDueFilter] = useState(sp.get("due") || "all"); // "all"|"overdue"|"today"|"week"
  const [attention, setAttention] = useState(sp.get("attention") || "all"); // "all"|"not_attended"|"seen_idle"
  const [level, setLevel] = useState(sp.get("level") || "all"); // "all"|"main"|"sub"|"subsub"
  const [department, setDepartment] = useState(sp.get("department") || "");
  const [teamPeople, setTeamPeople] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [page, setPage] = useState(Math.max(1, parseInt(sp.get("page") || "1", 10) || 1));
  const [perPage, setPerPage] = useState(() => {
    const p = parseInt(sp.get("per_page") || "5", 10);
    return [5, 10, 25, 50].includes(p) ? p : 5;
  });
  const fileRef = useRef(null);

  const [flatOverride] = useState(() => sp.get("flat") === "1");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("view", view);
      // Any active filter should surface every actionable row (main + sub + sub-sub) to match
      // dashboard counting (§9). Only the fully-unfiltered browse view keeps the parent-first hierarchy.
      // ?flat=1 explicit override (used by the "Total tasks" dashboard tile).
      const anyFilter = flatOverride || view === "completed" || dueFilter !== "all" || level !== "all" || status !== "all"
        || attention !== "all" || assignee !== "all" || client || entity !== "all"
        || type !== "all" || service !== "all" || q || department;
      if (!anyFilter) params.set("only_root", "true");
      params.set("scope", scope);
      if (status !== "all") params.set("status", status);
      if (assignee !== "all") params.set("assignee_id", assignee);
      if (client) params.set("client_name", client);
      if (entity !== "all") params.set("entity", entity);
      if (type !== "all") params.set("task_type", type);
      if (service !== "all") params.set("service_category", service);
      if (dueFilter !== "all") params.set("due", dueFilter);
      if (attention !== "all") params.set("attention", attention);
      if (level !== "all") params.set("level", level);
      if (q) params.set("q", q);
      if (department) params.set("department_id", department);
      const [t, a, ty, sc, en] = await Promise.all([
        api.get(`/tasks?${params}`),
        api.get("/users/assignable"),
        api.get("/task-types"),
        api.get("/service-categories"),
        api.get("/entities"),
      ]);
      setTasks(t.data);
      setAssignables(a.data);
      setTypes(ty.data);
      setServices(sc.data);
      setEntities(en.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  }, [view, scope, status, assignee, client, entity, type, service, q, dueFilter, attention, level, flatOverride, department]);
  useEffect(() => { load(); }, [load]);
  // Clear bulk selection when scope or view changes
  useEffect(() => { setSelected(new Set()); }, [view, scope]);

  // Default admins to team scope when user info loads (unless URL forced mine)
  useEffect(() => {
    if (isManager(user) && scope === "mine" && !sp.get("scope")) setScope("team");
    // eslint-disable-next-line
  }, [user?.role]);

  useEffect(() => { if (bulkOpen) api.get("/users/assignable?team=1").then((r) => setTeamPeople(r.data)).catch(() => {}); }, [bulkOpen]);

  // URL sync
  useEffect(() => {
    const p = {};
    if (view !== "active") p.view = view;
    if (scope !== "mine") p.scope = scope;
    if (status !== "all") p.status = status;
    if (assignee !== "all") p.assignee = assignee;
    if (client) p.client = client;
    if (entity !== "all") p.entity = entity;
    if (type !== "all") p.type = type;
    if (service !== "all") p.service = service;
    if (dueFilter !== "all") p.due = dueFilter;
    if (attention !== "all") p.attention = attention;
    if (level !== "all") p.level = level;
    if (department) p.department = department;
    if (flatOverride) p.flat = "1";
    if (q) p.q = q;
    if (page > 1) p.page = String(page);
    if (perPage !== 5) p.per_page = String(perPage);
    setSp(p, { replace: true });
    // eslint-disable-next-line
  }, [view, scope, status, assignee, client, entity, type, service, q, dueFilter, attention, level, page, perPage, department]);

  // Reset to page 1 whenever filters or dataset change (but not page itself)
  const lastFilterKey = useRef(null);
  useEffect(() => {
    // Keep a deep-linked ?page=N on first render; reset only when a filter value actually changes.
    const key = JSON.stringify([view, scope, status, assignee, client, entity, type, service, q, dueFilter, attention, level, perPage]);
    if (lastFilterKey.current !== null && lastFilterKey.current !== key) setPage(1);
    lastFilterKey.current = key;
  }, [view, scope, status, assignee, client, entity, type, service, q, dueFilter, attention, level, perPage]);

  const totalPages = Math.max(1, Math.ceil(tasks.length / perPage));
  const clampedPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (clampedPage - 1) * perPage;
    return tasks.slice(start, start + perPage);
  }, [tasks, clampedPage, perPage]);
  const rangeStart = tasks.length === 0 ? 0 : (clampedPage - 1) * perPage + 1;
  const rangeEnd = Math.min(clampedPage * perPage, tasks.length);
  const pageNumbers = useMemo(() => buildPageList(clampedPage, totalPages), [clampedPage, totalPages]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/tasks-export.csv?view=${view}&scope=${scope}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `tasks-export-${scope}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/tasks-import-template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "tasks-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const uploadImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/tasks-import", form, { headers: { "Content-Type": "multipart/form-data" } });
      const errCount = data.error_count || 0;
      if (errCount) {
        toast.warning(`Imported ${data.imported}. ${errCount} row(s) skipped — check console.`);
        console.warn("Task import errors", data.errors);
      } else {
        toast.success(`Imported ${data.imported} tasks`);
      }
      setImportOpen(false);
      load();
    } catch (er) { toast.error(formatErr(er)); }
  };

  // ---- Bulk selection helpers ----
  const toggleRow = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (s.size === tasks.length) return new Set();
      return new Set(tasks.map((t) => t.id));
    });
  };
  const clearSelection = () => setSelected(new Set());

  const submitBulkReassign = async () => {
    if (!bulkAssignee) { toast.error("Pick a new assignee"); return; }
    setBulkSubmitting(true);
    try {
      const { data } = await api.post("/tasks/bulk-assign", {
        task_ids: Array.from(selected),
        new_assignee_id: bulkAssignee,
      });
      toast.success(`Reassigned ${data.reassigned} task(s) to ${data.new_assignee_name}`);
      setBulkOpen(false); setBulkAssignee(""); clearSelection();
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBulkSubmitting(false); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Workspace</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Tasks</h1>
        </div>
        <Button data-testid="task-add-btn" onClick={() => setDialogOpen(true)} className="bg-blue-700 hover:bg-blue-800 text-white gap-2">
          <Plus className="w-4 h-4" /> New task
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit">
          <button data-testid="task-view-active" onClick={() => setView("active")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${view === "active" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><ListChecks className="w-3.5 h-3.5" /> Active</button>
          <button data-testid="task-view-completed" onClick={() => setView("completed")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${view === "completed" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><Archive className="w-3.5 h-3.5" /> Completed</button>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit">
            <button data-testid="task-scope-mine" onClick={() => setScope("mine")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "mine" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><User className="w-3.5 h-3.5" /> Mine</button>
            <button data-testid="task-scope-team" onClick={() => setScope("team")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition ${scope === "team" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"}`}><Users className="w-3.5 h-3.5" /> Team</button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button data-testid="task-import-btn" variant="outline" size="sm" onClick={() => setImportOpen(true)} className="gap-2"><Upload className="w-3.5 h-3.5" /> Import CSV</Button>
        <Button data-testid="task-export-btn" variant="outline" size="sm" onClick={exportCsv} className="gap-2"><Download className="w-3.5 h-3.5" /> Export CSV</Button>
        {isAdmin && <HandoverDialog onDone={load} />}
        {department && <DeptFilterChip id={department} onClear={() => setDepartment("")} />}
      </div>
      <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={uploadImport} data-testid="task-import-input" />

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="lg:col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="task-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by task name…" className="pl-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="task-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="attended">Attended</SelectItem>
            <SelectItem value="in-progress">In Progress</SelectItem>
            <SelectItem value="awaiting">Awaiting</SelectItem>
            <SelectItem value="on-hold">On-Hold</SelectItem>
            <SelectItem value="done">Done</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dueFilter} onValueChange={setDueFilter}>
          <SelectTrigger data-testid="task-filter-due"><SelectValue placeholder="Deadline" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All deadlines</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="today">Due today</SelectItem>
            <SelectItem value="week">Due this week</SelectItem>
          </SelectContent>
        </Select>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger data-testid="task-filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="main">Main tasks</SelectItem>
            <SelectItem value="sub">Sub-tasks</SelectItem>
            <SelectItem value="subsub">Sub-sub-tasks</SelectItem>
          </SelectContent>
        </Select>
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger data-testid="task-filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            {assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="task-filter-client" value={client} onChange={(e) => setClient(e.target.value)} placeholder="Client name" />
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="task-filter-entity"><SelectValue placeholder="Entity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All entities</SelectItem>
            {entities.map((e) => <SelectItem key={e.id} value={e.name}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="task-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={service} onValueChange={setService}>
          <SelectTrigger data-testid="task-filter-service"><SelectValue placeholder="Service" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isAdmin && selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="mb-3 flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-md px-4 py-2">
          <div className="text-sm text-indigo-900"><b>{selected.size}</b> task{selected.size > 1 ? "s" : ""} selected</div>
          <div className="flex items-center gap-2">
            <Button data-testid="bulk-reassign-btn" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1" onClick={() => setBulkOpen(true)}><Users className="w-3.5 h-3.5" /> Reassign selected</Button>
            <Button data-testid="bulk-clear-btn" size="sm" variant="ghost" onClick={clearSelection}>Clear</Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className={`grid ${isAdmin ? "grid-cols-13" : "grid-cols-12"} gap-2 px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500`} style={isAdmin ? { gridTemplateColumns: "24px repeat(12, minmax(0, 1fr))" } : undefined}>
          {isAdmin && (
            <div>
              <input
                type="checkbox"
                data-testid="bulk-select-all"
                checked={tasks.length > 0 && selected.size === tasks.length}
                onChange={toggleAll}
                className="cursor-pointer"
              />
            </div>
          )}
          <div className="col-span-3">Task</div>
          <div className="col-span-2">Client · Entity</div>
          <div className="col-span-2">Assignee</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1 text-right" title="Total effort (incl. sub-tasks)">Effort</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {loading ? <div className="p-8 text-center text-sm text-slate-500">Loading…</div> :
         tasks.length === 0 ? <div className="p-10 text-center text-sm text-slate-500">No tasks yet.</div> : (
          <ul className="divide-y divide-slate-200">
            {paged.map((t) => (
              <li
                key={t.id}
                data-testid={`task-row-${t.id}`}
                onClick={(e) => {
                  // Ignore click when it originates from the checkbox
                  if (e.target?.tagName === "INPUT") return;
                  nav(`/tasks/${t.id}`);
                }}
                className={`grid ${isAdmin ? "" : "grid-cols-12"} gap-2 px-5 py-3 items-start cursor-pointer hover:bg-slate-50`}
                style={isAdmin ? { gridTemplateColumns: "24px repeat(12, minmax(0, 1fr))" } : undefined}
              >
                {isAdmin && (
                  <div>
                    <input
                      type="checkbox"
                      data-testid={`bulk-select-${t.id}`}
                      checked={selected.has(t.id)}
                      onChange={(e) => { e.stopPropagation(); toggleRow(t.id); }}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-pointer"
                    />
                  </div>
                )}
                <div className="col-span-3 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {/* Hierarchy level chip (spec §7) */}
                    {t.depth === 0 ? (
                      <span data-testid={`level-chip-${t.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">Main</span>
                    ) : t.depth === 1 ? (
                      <span data-testid={`level-chip-${t.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">Sub-task</span>
                    ) : (
                      <span data-testid={`level-chip-${t.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200">Sub-sub</span>
                    )}
                    {t.recurrence && t.recurrence !== "none" && (
                      <span data-testid={`task-recurrence-${t.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-teal-50 text-teal-700 border border-teal-200">
                        {t.recurrence === "weekly" ? "Weekly" : "Monthly"}
                      </span>
                    )}
                    {/* Rollup: how many descendants are overdue? (spec §2) */}
                    {t.subtask_summary?.overdue > 0 && (
                      <span data-testid={`subtask-overdue-${t.id}`} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200">
                        {t.subtask_summary.overdue} overdue sub-task{t.subtask_summary.overdue > 1 ? "s" : ""}
                      </span>
                    )}
                    {t.subtask_count > 0 && !t.subtask_summary?.overdue && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200">{t.subtask_count} subtask{t.subtask_count > 1 ? "s" : ""}</span>}
                    {t.attachment_count > 0 && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-sky-50 text-sky-700 border border-sky-200">{t.attachment_count} file{t.attachment_count > 1 ? "s" : ""}</span>}
                  </div>
                  <div data-testid={`task-name-${t.id}`} className="font-medium text-slate-900 mt-1 break-words" title={t.name}>{t.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    {t.parent_task_name && <span className="text-slate-400">Parent · <b className="text-slate-600">{t.parent_task_name}</b> · </span>}
                    {t.task_type} · {t.service_category || "—"} · Created by {t.creator_name}
                  </div>
                </div>
                <div className="col-span-2 text-xs">
                  <div className="truncate">{t.client_name || "—"}</div>
                  <div className="text-slate-500 truncate">{t.entity || "—"}</div>
                </div>
                <div className="col-span-2 text-xs truncate">{t.assignee_name || <span className="text-slate-400">Unassigned</span>}</div>
                <div className="col-span-2 mono text-xs flex items-center gap-1.5">
                  {t.due_date ? (
                    <>
                      <span>{t.due_date}</span>
                      {t.is_active !== false && t.due_date < new Date().toISOString().slice(0, 10) && (
                        <span data-testid={`overdue-badge-${t.id}`} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200">Overdue</span>
                      )}
                    </>
                  ) : <span className="text-slate-400">—</span>}
                </div>
                <div className="col-span-1 text-right mono text-xs" data-testid={`task-effort-${t.id}`}
                     title={t.subtask_count > 0 ? "Total incl. sub-tasks" : "Own time"}>
                  {(() => {
                    const s = Math.max(0, Math.floor(t.total_seconds_rollup ?? t.total_seconds ?? 0));
                    const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
                    return h > 0 ? `${h}h ${String(m).padStart(2,'0')}m` : `${m}m`;
                  })()}
                </div>
                <div className="col-span-2 flex justify-end items-center gap-2">
                  <span data-testid={`task-status-${t.id}`} className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_STYLES[t.status] || STATUS_STYLES.pending}`}>{t.status.replace("-", " ")}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </li>
            ))}
          </ul>
         )}
        {!loading && tasks.length > 0 && (
          <div data-testid="tasks-pagination-bar" className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-slate-200 bg-slate-50/40">
            <div className="text-xs text-slate-600 mono">
              Showing <b>{rangeStart}</b>–<b>{rangeEnd}</b> of <b>{tasks.length}</b>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>Rows</span>
                <Select value={String(perPage)} onValueChange={(v) => setPerPage(parseInt(v, 10))}>
                  <SelectTrigger data-testid="tasks-per-page-select" className="h-8 w-[72px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  data-testid="tasks-page-prev"
                  variant="outline"
                  size="sm"
                  className="h-8 px-2"
                  disabled={clampedPage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {pageNumbers.map((n, i) =>
                  n === "…" ? (
                    <span key={`e-${i}`} className="px-2 text-xs text-slate-400 select-none">…</span>
                  ) : (
                    <Button
                      key={n}
                      data-testid={`tasks-page-btn-${n}`}
                      variant={n === clampedPage ? "default" : "outline"}
                      size="sm"
                      className={`h-8 w-8 p-0 mono text-xs ${n === clampedPage ? "bg-blue-700 hover:bg-blue-800 text-white" : ""}`}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </Button>
                  )
                )}
                <Button
                  data-testid="tasks-page-next"
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 gap-1"
                  disabled={clampedPage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <NewTaskDialog open={dialogOpen} onOpenChange={setDialogOpen} assignables={assignables} types={types} services={services} entities={entities} currentUserId={user?.id} onCreated={load} />

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Import tasks from CSV</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Upload a CSV file matching the template. Assignee is resolved by email; leave the column blank to keep the task assigned to you.</p>
            <ul className="text-xs text-slate-500 list-disc list-inside space-y-1">
              <li>Required columns: <span className="mono">name</span>, <span className="mono">task_date</span> (YYYY-MM-DD)</li>
              <li>Optional: task_time, client_name, entity, task_type, service_category, description, due_date, notes, recurrence, recurrence_end_date, assignee_email</li>
              <li>Recurrence values: <span className="mono">none</span>, <span className="mono">weekly</span>, or <span className="mono">monthly</span></li>
            </ul>
            <div className="flex gap-2">
              <Button data-testid="task-import-template-btn" variant="outline" size="sm" onClick={downloadTemplate}><Download className="w-3.5 h-3.5 mr-1" /> Download template</Button>
              <Button data-testid="task-import-choose-btn" onClick={() => fileRef.current?.click()} className="bg-blue-700 hover:bg-blue-800 text-white gap-1"><Upload className="w-3.5 h-3.5" /> Choose file…</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reassign {selected.size} task{selected.size > 1 ? "s" : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Handover the selected tasks to another teammate. Their status resets so the new assignee sees them as fresh Pending items (unless self-assigned).</p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">New assignee</Label>
              <Select value={bulkAssignee} onValueChange={setBulkAssignee}>
                <SelectTrigger data-testid="bulk-assignee-select" className="mt-1"><SelectValue placeholder="Choose a user…" /></SelectTrigger>
                <SelectContent>
                  {teamPeople.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-xs text-slate-400 ml-2 mono">{u.email}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button data-testid="bulk-submit" onClick={submitBulkReassign} disabled={bulkSubmitting || !bulkAssignee} className="bg-blue-700 hover:bg-blue-800 text-white">
              {bulkSubmitting ? "Reassigning…" : "Confirm reassign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function NewTaskDialog({ open, onOpenChange, assignables, types, services, entities, currentUserId, parentTaskId, parentTask, editTask, onCreated }) {
  const [name, setName] = useState("");
  const [taskDate, setTaskDate] = useState(new Date().toISOString().slice(0, 10));
  const [taskTime, setTaskTime] = useState("");
  const [client, setClient] = useState("");
  const [entity, setEntity] = useState("__none__");
  const [type, setType] = useState("Retainership");
  const [service, setService] = useState("__none__");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [assignee, setAssignee] = useState(currentUserId || "");
  const [recurrence, setRecurrence] = useState("none");
  const [recurrenceEnd, setRecurrenceEnd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Inline quick-add for entity + service category (avoids trip to Master settings)
  const [newEntities, setNewEntities] = useState([]);
  const [newServices, setNewServices] = useState([]);
  const [quickAdd, setQuickAdd] = useState(null); // { kind: 'entity' | 'service', value: '' }
  const [quickBusy, setQuickBusy] = useState(false);
  const isEdit = !!editTask;
  const isRoot = isEdit ? !editTask.parent_id : !parentTaskId;

  // Helpers: merge locally-added items with parent-provided lists
  const mergedEntities = useMemo(
    () => [...entities, ...newEntities.filter((n) => !entities.some((e) => e.name === n.name))],
    [entities, newEntities]
  );
  const mergedServices = useMemo(
    () => [...services, ...newServices.filter((n) => !services.some((s) => s.name === n.name))],
    [services, newServices]
  );

  const currentHHMM = () => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!open) return;
    if (editTask) {
      // Edit mode: pre-fill everything from the task being edited
      setName(editTask.name || "");
      setTaskDate(editTask.task_date || new Date().toISOString().slice(0, 10));
      setTaskTime(editTask.task_time || "");
      setClient(editTask.client_name || "");
      setEntity(editTask.entity || "__none__");
      setType(editTask.task_type || "Retainership");
      setService(editTask.service_category || "__none__");
      setDescription(editTask.description || "");
      setDueDate(editTask.due_date || "");
      setNotes(editTask.notes || "");
      setAssignee(editTask.assignee_id || currentUserId || "");
      setRecurrence(editTask.recurrence || "none");
      setRecurrenceEnd(editTask.recurrence_end_date || "");
    } else {
      // Create mode — default task time to NOW
      setTaskTime(currentHHMM());
      setAssignee(currentUserId || "");
      if (parentTask) {
        setClient(parentTask.client_name || "");
        setEntity(parentTask.entity || "__none__");
        setType(parentTask.task_type || "Retainership");
        setService(parentTask.service_category || "__none__");
      }
    }
  }, [open, currentUserId, parentTask, editTask]);

  const openQuickAdd = (kind) => setQuickAdd({ kind, value: "" });

  const submitQuickAdd = async () => {
    const val = (quickAdd?.value || "").trim();
    if (!val) { toast.error("Name is required"); return; }
    setQuickBusy(true);
    try {
      if (quickAdd.kind === "entity") {
        const { data } = await api.post("/entities", { name: val });
        setNewEntities((prev) => [...prev, data]);
        setEntity(data.name);
        toast.success("Entity added");
      } else {
        const { data } = await api.post("/service-categories", { name: val });
        setNewServices((prev) => [...prev, data]);
        setService(data.name);
        toast.success("Service category added");
      }
      setQuickAdd(null);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setQuickBusy(false); }
  };

  const submit = async () => {
    if (!name.trim() || !taskDate) { toast.error("Task name and date are required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(), task_date: taskDate, task_time: taskTime || null,
        client_name: client.trim() || null,
        entity: entity === "__none__" ? null : entity,
        task_type: type,
        service_category: service === "__none__" ? null : service,
        description: description.trim() || null,
        due_date: dueDate || null,
        notes: notes.trim() || null,
        assignee_id: assignee || null,
      };
      if (isEdit) {
        // Only send recurrence fields when the task is a root task
        if (isRoot) {
          payload.recurrence = recurrence;
          payload.recurrence_end_date = recurrence !== "none" && recurrenceEnd ? recurrenceEnd : null;
        }
        await api.patch(`/tasks/${editTask.id}`, payload);
        toast.success("Task updated");
      } else {
        payload.parent_id = parentTaskId || null;
        payload.recurrence = isRoot ? recurrence : "none";
        payload.recurrence_end_date = isRoot && recurrence !== "none" && recurrenceEnd ? recurrenceEnd : null;
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      onOpenChange(false);
      if (!isEdit) {
        setName(""); setDescription(""); setNotes(""); setDueDate("");
        if (isRoot) { setClient(""); setEntity("__none__"); setService("__none__"); }
        setRecurrence("none"); setRecurrenceEnd("");
      }
      onCreated?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : (parentTaskId ? "New sub-task" : "New task")}</DialogTitle>
        </DialogHeader>
        {!isEdit && parentTask && (
          <div data-testid="inherited-summary" className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-600 mb-2">
            Inheriting from parent: <b>{parentTask.client_name || "—"}</b> · <b>{parentTask.entity || "—"}</b> · <b>{parentTask.task_type}</b>{parentTask.service_category ? <> · <b>{parentTask.service_category}</b></> : null}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Task name</Label>
            <Input data-testid="new-task-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" required />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Task date</Label>
            <Input data-testid="new-task-date" type="date" value={taskDate} onChange={(e) => setTaskDate(e.target.value)} className="mt-1 mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Time</Label>
            <Input data-testid="new-task-time" type="time" value={taskTime} onChange={(e) => setTaskTime(e.target.value)} className="mt-1 mono" />
          </div>
          {isRoot && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Client name</Label>
                <Input data-testid="new-task-client" value={client} onChange={(e) => setClient(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Entity</Label>
                <Select value={entity} onValueChange={(v) => v === "__quick_add__" ? openQuickAdd("entity") : setEntity(v)}>
                  <SelectTrigger data-testid="new-task-entity" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {mergedEntities.map((e) => <SelectItem key={e.id || e.name} value={e.name}>{e.name}</SelectItem>)}
                    <SelectItem value="__quick_add__" data-testid="entity-quick-add">
                      <span className="text-emerald-700 font-medium">+ Add new entity…</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger data-testid="new-task-type" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Service category</Label>
                <Select value={service} onValueChange={(v) => v === "__quick_add__" ? openQuickAdd("service") : setService(v)}>
                  <SelectTrigger data-testid="new-task-service" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {mergedServices.map((s) => <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>)}
                    <SelectItem value="__quick_add__" data-testid="service-quick-add">
                      <span className="text-emerald-700 font-medium">+ Add new service category…</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Due date</Label>
            <Input data-testid="new-task-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 mono" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Assignee</Label>
            <Select value={assignee} onValueChange={setAssignee}>
              <SelectTrigger data-testid="new-task-assignee" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.id === currentUserId ? " (me)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
            <textarea data-testid="new-task-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="mt-1 w-full text-sm border border-slate-200 rounded-sm px-3 py-2 focus:outline-none focus:ring-1 focus:ring-slate-400" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
            <textarea data-testid="new-task-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full text-sm border border-slate-200 rounded-sm px-3 py-2" />
          </div>
          {isRoot && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Recurrence</Label>
                <Select value={recurrence} onValueChange={setRecurrence}>
                  <SelectTrigger data-testid="new-task-recurrence" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">One-off (no repeat)</SelectItem>
                    <SelectItem value="weekly">Repeat weekly</SelectItem>
                    <SelectItem value="monthly">Repeat monthly</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Next occurrence is auto-created when you mark Done.</p>
              </div>
              {recurrence !== "none" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Repeat until (optional)</Label>
                  <Input data-testid="new-task-recurrence-end" type="date" value={recurrenceEnd} onChange={(e) => setRecurrenceEnd(e.target.value)} className="mt-1 mono" />
                  <p className="text-[11px] text-slate-500 mt-1">Leave blank to repeat forever.</p>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-task-submit" onClick={submit} disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white">
            {submitting ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create task")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Quick-add: inline entity / service category creation */}
      <Dialog open={!!quickAdd} onOpenChange={(o) => { if (!o && !quickBusy) setQuickAdd(null); }}>
        <DialogContent data-testid="quick-add-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {quickAdd?.kind === "entity" ? "Add new entity" : "Add new service category"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              {quickAdd?.kind === "entity" ? "Entity name" : "Service category name"}
            </Label>
            <Input
              data-testid="quick-add-input"
              value={quickAdd?.value || ""}
              onChange={(e) => setQuickAdd((q) => ({ ...q, value: e.target.value }))}
              placeholder={quickAdd?.kind === "entity" ? "e.g. ABC Consulting LLP" : "e.g. Advisory"}
              className="mt-1"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitQuickAdd(); } }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickAdd(null)} disabled={quickBusy}>Cancel</Button>
            <Button
              data-testid="quick-add-submit"
              onClick={submitQuickAdd}
              disabled={quickBusy || !(quickAdd?.value || "").trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {quickBusy ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => pages.add(n));
  if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((n) => pages.add(n));
  const sorted = Array.from(pages).filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}
